/**
 * Format a date string to a localized format
 */
export function formatDate(dateString: string, locale: string = 'fr-FR'): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

/**
 * Format a date string to a short localized format
 */
export function formatDateShort(dateString: string, locale: string = 'fr-FR'): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/**
 * Truncate text to a maximum length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Generate a slug from a string
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>

  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Check if object is empty
 */
export function isEmpty(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0
}

/**
 * Convert image path to API endpoint URL
 * Handles uploads through the API proxy to ensure proper CORS headers
 */
export function getImageUrl(path: string): string {
  if (!path) return ''

  // If it's a data URL, return as-is
  if (path.startsWith('data:')) return path

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

  // If it's a full URL pointing to uploads, convert it to API endpoint
  if (path.startsWith('http')) {
    // Extract the uploads path from full URL
    const uploadsMatch = path.match(/\/uploads\/(.+)$/)
    if (uploadsMatch) {
      return `${API_URL}/media/serve/${uploadsMatch[1]}`
    }
    // If it's not an uploads URL, return as-is
    return path
  }

  // Convert uploads/ paths to use the new API endpoint
  // e.g., "uploads/media/2025/11/file.jpg" -> "/api/media/serve/media/2025/11/file.jpg"
  if (path.startsWith('uploads/')) {
    const pathWithoutUploads = path.replace('uploads/', '')
    return `${API_URL}/media/serve/${pathWithoutUploads}`
  }

  // For backward compatibility with paths that don't start with uploads/
  if (path.startsWith('/uploads/')) {
    const pathWithoutUploads = path.replace('/uploads/', '')
    return `${API_URL}/media/serve/${pathWithoutUploads}`
  }

  // For any other path, use the API endpoint
  return `${API_URL}/media/serve/${path.replace(/^\//, '')}`
}
